import { faStopwatch } from "@fortawesome/pro-regular-svg-icons";
import { faThumbtack } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { History, Location } from "history";
import cloneDeep from "lodash-es/cloneDeep";
import React from "react";
import { connect } from "react-redux";

import { blogPostSetCommentsOpened } from "@/mk2/apps/blogs/components/BlogPost/BlogPost.actions";
import { BlogPostState } from "@/mk2/apps/blogs/components/BlogPost/BlogPost.reducers";
import { getBlogPostState } from "@/mk2/apps/blogs/components/BlogPost/BlogPost.selectors";
import BlogPostMenu from "@/mk2/apps/blogs/components/BlogPostMenu";
import BlogPostPhotos from "@/mk2/apps/blogs/components/BlogPostPhotos";
import BlogPostVisibilityMenu from "@/mk2/apps/blogs/components/BlogPostVisibilityMenu";
import CommentsButton from "@/mk2/apps/blogs/components/CommentsButton";
import ShareButton from "@/mk2/apps/blogs/components/ShareButton";
import { CommentsDesign } from "@/mk2/apps/blogs/constants";
import {
    Difficulty,
    GridRecipeCard,
} from "@/mk2/apps/blogs/containers/AdvancedArticlesPreviewGrid/AdvancedArticlesPreviewGrid";
import Comments from "@/mk2/apps/blogs/containers/Comments/Comments";
import { getPostDate } from "@/mk2/apps/blogs/helpers";
import {
    RECIPE_ARTICLE_TYPE_SLUG,
    advancedArticleUrl,
    blogsAdvancedArticleUrl,
    blogsAlbumDetailUrl,
    blogsArticleUrl,
    blogsFlaggedFeedUrl,
    blogsFlaggedGroupsFeedUrl,
    blogsPostDetailUrl,
    blogsPostShareUrl,
    blogsProfileUrl,
    blogsUrl,
} from "@/mk2/apps/blogs/urls";
import {
    getGroupHashtagLink,
    getShortMessageHashtagLink,
    userHasAdminPerm,
} from "@/mk2/apps/blogs/utils";
import { forumPostDetailUrl, forumUntrustworthyUserFeedUrl } from "@/mk2/apps/forum/urls";
import { groupsGroupUrl, groupsPostDetailUrl } from "@/mk2/apps/groups/urls";
import { CollapsedReview } from "@/mk2/apps/strollers/components/CollapsedReview";
import { dummyPhoto } from "@/mk2/apps/strollers/constants";
import { strollersDetailUrl } from "@/mk2/apps/strollers/urls";
import ExperienceBody from "@/mk2/apps/wiki/components/ExperienceBody";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { RenderSnippetAs } from "@/mk2/apps/wiki/utils";
import { AvatarImg } from "@/mk2/components/AvatarImg";
import Badge, { BadgeType } from "@/mk2/components/Badge";
import { Btn, BtnSidePadding, BtnType } from "@/mk2/components/Btn";
import { Clamp } from "@/mk2/components/Clamp";
import { DeletedPostAlert } from "@/mk2/components/DeletedPostAlert";
import { Dot } from "@/mk2/components/Dot";
import { Emojify } from "@/mk2/components/Emojify";
import { ExpandableClamp } from "@/mk2/components/ExpandableClamp";
import { ImgCropMode, ImgCropped } from "@/mk2/components/ImgCropped";
import { Link } from "@/mk2/components/Link";
import { StarsRating } from "@/mk2/components/StarsRating";
import Sticker, { StickerSize } from "@/mk2/components/Sticker";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import Complaints from "@/mk2/containers/Complaints/Complaints";
import Like, { LikeType } from "@/mk2/containers/Like/Like";
import { getBestVersionForThumb } from "@/mk2/decorators/withUploader";
import {
    FilterAdditionalData,
    ShowPhotosAndVideos,
    astToHtml,
    defaultFilterAdditionalData,
} from "@/mk2/helpers/article_ast/filters";
import {
    getAllDescendantsOfTypes,
    getAllPlaintext,
    removeParagraphsAndLinesWithoutPlaintext,
} from "@/mk2/helpers/article_ast/utils";
import { cacheLast } from "@/mk2/helpers/cache";
import { postDeletedByToText } from "@/mk2/helpers/deleteable";
import { UserAgentParsed } from "@/mk2/helpers/detects";
import { findCoverPhotoEntityFromAst } from "@/mk2/helpers/structuredData";
import { YMDHMSIso, dateAt, dateDiff, timeAt } from "@/mk2/helpers/timeformat";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import { AppState } from "@/mk2/reducers";
import {
    AST,
    BlogFeedItemEntity,
    BlogPostEntity,
    BlogPostType,
    CommentEntity,
    PostVisibility,
    UserEntity,
} from "@/mk2/schemas";
import { interpolate } from "@/mk2/services/i18n";
import {
    CHANGED_AVATAR,
    CHILD_ADDED,
    CHILD_ADDED_DAUGHTER,
    CHILD_ADDED_SON,
    CHILD_EDITED_DAUGHTER,
    CHILD_EDITED_SON,
    EXPERIENCE_ADDED,
    FORUM_THREAD_ADDED,
    GROUP_MODERATOR_SHORT,
    NEW_POST,
    NUMBER_OF_VIEWS_count,
    PB_POST_HAS_BEEN_EDITED,
    PB_SHARED_A_POST,
    POST_REMOVE_COMPLAINTS,
    READ_WHOLE_ARTICLE,
    RECIPE_ADDED,
    RECIPE_CREATE_FROM_POST_HINT,
    SHOW,
    SHOW_FULL_MESSAGE,
    counter_NEW_PHOTOS,
    counter_PHOTOS,
    number_MINUTES,
} from "@/mk/autogenerated/translation";
import { hashize } from "@/mk/bazaar/common/filters/hashize";
import { authorIsModerator, isAuthenticatedAs } from "@/mk/bazaar/common/userUtils";
import { anyToDate } from "@/mk/common/timeUtils";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./BlogPost.mscss";

const emptyCounts = {};

interface OwnState {
    stickyPostExpanded: boolean;
}

interface OwnProps {
    className?: string;
    comments?: CommentEntity[];
    commentsCount: number;
    complaintsCount?: number;
    disablePostAuthorBadges?: boolean;
    forceEagerImages?: boolean;
    groupModerators?: UserEntity[];
    imageComplaintsCounts?: { [imageId: number]: number };
    isMobile: boolean;
    isPreloaded?: boolean;
    location: Location;
    history: History;
    match?: any;
    parentFeedItem?: BlogFeedItemEntity;
    post: BlogPostEntity;
    postSubtreeComplaintIds?: number[];
    renderJustBody?: boolean;
    requestUser: UserEntity;
    requestUserPermissions: string[];
    showGroup?: boolean;
    isGroupWithRecipes?: boolean;
    showSticky?: boolean;
    showStrip?: boolean;
    userAgent?: UserAgentParsed;
    version?: number;

    onChangeSticky?(postId: number, sticky: boolean);
    onCreateRecipeFromPost?(postId: number);
    onDelete?(postId: number);
    onGroupLeave?(groupId: number);
    onHideGroupInFeed?(groupId: number);
    onIgnoreUser?(username: string);
    onReport?(entityType: string, commentId: number);
    onSetDisplayingAtFeed?(postId: number, displaying: boolean);
    onSetVisibility?(postId: number, visibility: PostVisibility);
    onToggleMenuMore(postId: number);
    onToggleVisibilityMenuMore?(postId: number);
}

interface StateProps {
    commentsOpened: boolean;
}

interface DispatchProps {
    onSetCommentsOpened(postId: number, commentsOpened: boolean);
}

type Props = OwnProps & StateProps & DispatchProps;

const PostDateLink = ({ post, justDate }: { post: BlogPostEntity; justDate?: boolean }) => {
    // constant "now" to provide proper snapshot comparison (on test env.) without dynamic PostDateLink
    const dNow = process.env.NODE_ENV === "test" ? new Date("2018-11-21T11:19:47.166Z") : null;

    const agoDate = getPostDate(post);
    const agoText = justDate ? dateAt(agoDate, dNow) : timeAt(agoDate, dNow);

    let gotoUrl;
    if (post.group) {
        gotoUrl = url(groupsPostDetailUrl, { groupId: post.group.id, postSlug: post.slug });
    } else if (post.advancedArticle) {
        gotoUrl = advancedArticleUrl(post);
    } else {
        gotoUrl = url(blogsPostDetailUrl(post.type), {
            username: post.author.username,
            postSlug: post.slug,
        });
    }

    return (
        <Link className={styles.BlogPost__body__meta__created} to={gotoUrl}>
            {agoText}
        </Link>
    );
};

class BlogPost extends React.PureComponent<Props, OwnState> {
    public static defaultProps: Partial<OwnProps> = {
        showGroup: false,
        showStrip: true,
    };

    private perexAstToHtmlCache = cacheLast(true); // used to cache post's rendered perex (article's title)
    private coverAstToHtmlCache = cacheLast(true); // used to cache article's rendered cover from AST
    private contentAstToHtmlCache = cacheLast(true); // used to cache article's rendered content from AST
    private advancedArticleOnelinerAstToHtmlCache = cacheLast(true);

    private messageFormTextarea: HTMLTextAreaElement;

    constructor(props: Props) {
        super(props);

        this.state = {
            stickyPostExpanded: !!props.isPreloaded,
        };

        const { isPreloaded, onSetCommentsOpened, post } = props;

        if (isPreloaded) {
            onSetCommentsOpened(post.id, !!isPreloaded);
        }
    }

    public render() {
        const { post, renderJustBody } = this.props;

        if (renderJustBody) {
            return this.renderBlogPostInner(post);
        }

        if (post.type === BlogPostType.ALBUM || post.type === BlogPostType.SHORT_MESSAGE) {
            return this.renderShortMessageOrAlbum();
        } else if (post.type === BlogPostType.ARTICLE) {
            return this.renderArticle();
        } else if (post.type === BlogPostType.AUTOMATIC_MESSAGE) {
            return this.renderAutomaticMessage();
        } else if (post.type === BlogPostType.SHARE) {
            return this.renderShare();
        } else {
            return this.renderShortMessageOrAlbum();
        }
    }

    public renderShortMessageOrAlbum() {
        const {
            className,
            commentsCount,
            commentsOpened,
            complaintsCount,
            forceEagerImages,
            groupModerators,
            imageComplaintsCounts,
            isMobile,
            isPreloaded,
            location,
            history,
            onChangeSticky,
            onCreateRecipeFromPost,
            onDelete,
            onGroupLeave,
            onHideGroupInFeed,
            onIgnoreUser,
            onSetDisplayingAtFeed,
            onReport,
            onSetVisibility,
            onToggleMenuMore,
            onToggleVisibilityMenuMore,
            parentFeedItem,
            post,
            requestUser,
            requestUserPermissions,
            showGroup,
            isGroupWithRecipes,
            showSticky,
            showStrip,
            userAgent,
        } = this.props;

        const avoidClampUse = userAgent?.flags?.buggyClamp;

        const showReply = true;
        const showLike = true;
        const showShare = post.author.username !== requestUser.username && !post.group;

        const showVisibilityMenu = !!onToggleVisibilityMenuMore;
        const appearSticky = showSticky && post.sticky;
        const stickyPostExpanded = !appearSticky || this.state.stickyPostExpanded || avoidClampUse;
        const inFeed = !isPreloaded;

        const postAuthorIsModerator = groupModerators
            ? authorIsModerator(post.author, groupModerators)
            : false;
        const isModerator = groupModerators
            ? authorIsModerator(requestUser, groupModerators)
            : false;
        const isGroupsAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const isBlogsAdmin = requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0;
        const isAuthor = isAuthenticatedAs(requestUser, post.author);
        const canChangeVisibility = showVisibilityMenu && (isAuthor || isBlogsAdmin);

        let role;
        if (isGroupsAdmin || isBlogsAdmin) {
            role = "admin";
        } else if (isModerator) {
            role = "moderator";
        } else if (isAuthor) {
            role = "author";
        }

        let wholeAlbumIsNew = false;
        let newImages = null;
        let albumChanged = false;
        if (post.type === BlogPostType.ALBUM) {
            const now = new Date();
            wholeAlbumIsNew = dateDiff(anyToDate(post.created), now).diffDays < 2;
            if (!wholeAlbumIsNew) {
                albumChanged =
                    dateDiff(anyToDate(post.created), anyToDate(post.lastChanged)).diffDays > 1 &&
                    dateDiff(anyToDate(post.lastChanged), now).diffDays < 7;
                if (albumChanged) {
                    let latestImageCreatedDate = null;
                    for (const image of post.images) {
                        latestImageCreatedDate =
                            latestImageCreatedDate === null
                                ? image.createdTime
                                : image.createdTime > latestImageCreatedDate
                                  ? image.createdTime
                                  : latestImageCreatedDate;
                    }
                    if (latestImageCreatedDate) {
                        const latestImageCreatedDay = anyToDate(latestImageCreatedDate);
                        latestImageCreatedDay.setHours(0);
                        latestImageCreatedDay.setMinutes(0);
                        latestImageCreatedDay.setSeconds(0);
                        latestImageCreatedDay.setMilliseconds(0);
                        const latestImageCreatedDayStr = YMDHMSIso(latestImageCreatedDay);
                        newImages = post.images.filter(
                            (image) => image.createdTime >= latestImageCreatedDayStr,
                        );
                    }
                }
            }
        }

        const blogPostMenu = (
            <BlogPostMenu
                className={styles.BlogPost__body__meta__dropdownMenu}
                parentFeedItem={parentFeedItem}
                post={post}
                requestUser={requestUser}
                location={location}
                hasDelete
                hasEdit
                hasLeave={showGroup}
                hasHideGroupInFeed={showGroup}
                hasReport
                isAdmin={isBlogsAdmin || isGroupsAdmin}
                isAuthor={isAuthor}
                isModerator={isModerator}
                onChangeSticky={onChangeSticky}
                onDelete={onDelete}
                onGroupLeave={onGroupLeave}
                onHideGroupInFeed={onHideGroupInFeed}
                onIgnoreUser={onIgnoreUser}
                onReport={onReport}
                onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                onToggleMenuMore={onToggleMenuMore}
            />
        );

        return post.deletedAt && !this.state.stickyPostExpanded ? (
            this.renderDeletedPost(post, className)
        ) : (
            <div
                className={cx(
                    styles.BlogPost,
                    commentsOpened && styles["BlogPost--commentsOpen"],
                    className,
                )}
            >
                {this.renderShortMessageOrAlbumOrAutomsgInner(
                    post,
                    blogPostMenu,
                    isMobile,
                    forceEagerImages,
                    showStrip,
                    showGroup,
                    stickyPostExpanded,
                    appearSticky,
                    postAuthorIsModerator,
                    showVisibilityMenu,
                    canChangeVisibility,
                    onSetVisibility,
                    onToggleVisibilityMenuMore,
                    inFeed,
                    imageComplaintsCounts,
                    newImages,
                    wholeAlbumIsNew,
                    null,
                    isGroupWithRecipes,
                    onCreateRecipeFromPost,
                    requestUser,
                )}

                {this.renderComplaints(post, role)}

                {this.renderFooter(
                    stickyPostExpanded,
                    showLike,
                    post,
                    showReply,
                    commentsCount,
                    showShare,
                    complaintsCount,
                    isBlogsAdmin || isGroupsAdmin,
                    isModerator,
                )}

                {commentsOpened && (
                    <Comments
                        parentPost={post}
                        count={commentsCount}
                        design={CommentsDesign.Normal}
                        location={location}
                        history={history}
                        moderators={groupModerators}
                        hasBlueToolbar={!inFeed}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        isMobile={isMobile}
                        onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                    />
                )}
            </div>
        );
    }

    public renderArticle() {
        const {
            className,
            commentsCount,
            commentsOpened,
            complaintsCount,
            forceEagerImages,
            groupModerators,
            isMobile,
            isPreloaded,
            location,
            history,
            onChangeSticky,
            onDelete,
            onGroupLeave,
            onHideGroupInFeed,
            onIgnoreUser,
            onSetDisplayingAtFeed,
            onReport,
            onToggleMenuMore,
            parentFeedItem,
            post,
            requestUser,
            requestUserPermissions,
            showGroup,
            showSticky,
            userAgent,
        } = this.props;

        const avoidClampUse = userAgent?.flags?.buggyClamp;

        const showReply = true;
        const showLike = true;
        const showShare = post.author !== requestUser && !post.group;

        const appearSticky = showSticky && post.sticky;
        const stickyPostExpanded = !appearSticky || this.state.stickyPostExpanded || avoidClampUse;
        const inFeed = !isPreloaded;

        const isModerator = groupModerators
            ? authorIsModerator(requestUser, groupModerators)
            : false;
        const isGroupsAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const isBlogsAdmin = requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0;
        const isAuthor = isAuthenticatedAs(requestUser, post.author);

        let role;
        if (isGroupsAdmin || isBlogsAdmin) {
            role = "admin";
        } else if (isModerator) {
            role = "moderator";
        } else if (isAuthor) {
            role = "author";
        }

        const blogPostMenu = (
            <BlogPostMenu
                className={styles.BlogPost__body__meta__dropdownMenu}
                parentFeedItem={parentFeedItem}
                post={post}
                requestUser={requestUser}
                location={location}
                hasDelete
                hasEdit
                hasLeave={showGroup}
                hasHideGroupInFeed={showGroup}
                hasReport
                isAdmin={isBlogsAdmin || isGroupsAdmin}
                isAuthor={isAuthor}
                isModerator={isModerator}
                onChangeSticky={onChangeSticky}
                onDelete={onDelete}
                onGroupLeave={onGroupLeave}
                onHideGroupInFeed={onHideGroupInFeed}
                onIgnoreUser={onIgnoreUser}
                onReport={onReport}
                onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                onToggleMenuMore={onToggleMenuMore}
            />
        );

        return post.deletedAt && !this.state.stickyPostExpanded ? (
            this.renderDeletedPost(post, className)
        ) : (
            <div
                className={cx(
                    styles.BlogPost,
                    commentsOpened && styles["BlogPost--commentsOpen"],
                    className,
                )}
            >
                {this.renderArticleInner(
                    post,
                    blogPostMenu,
                    isMobile,
                    requestUserPermissions,
                    forceEagerImages,
                )}

                {this.renderComplaints(post, role)}

                {this.renderFooter(
                    stickyPostExpanded,
                    showLike,
                    post,
                    showReply,
                    commentsCount,
                    showShare,
                    complaintsCount,
                    isBlogsAdmin || isGroupsAdmin,
                    isModerator,
                )}

                {commentsOpened && (
                    <Comments
                        parentPost={post}
                        count={commentsCount}
                        design={CommentsDesign.Normal}
                        location={location}
                        history={history}
                        moderators={groupModerators}
                        hasBlueToolbar={!inFeed}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        isMobile={isMobile}
                        onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                    />
                )}
            </div>
        );
    }

    public renderShare() {
        const {
            className,
            commentsCount,
            commentsOpened,
            complaintsCount,
            groupModerators,
            isPreloaded,
            location,
            history,
            onChangeSticky,
            onDelete,
            onGroupLeave,
            onHideGroupInFeed,
            onIgnoreUser,
            onSetDisplayingAtFeed,
            onReport,
            onSetVisibility,
            onToggleMenuMore,
            onToggleVisibilityMenuMore,
            parentFeedItem,
            post,
            requestUser,
            requestUserPermissions,
            isMobile,
            showGroup,
            showSticky,
            userAgent,
        } = this.props;

        const avoidClampUse = userAgent?.flags?.buggyClamp;

        const showReply = true;
        const showLike = true;
        const showShare = post.author.username !== requestUser.username && !post.group;

        const showVisibilityMenu = !!onToggleVisibilityMenuMore;
        const appearSticky = showSticky && post.sticky;
        const stickyPostExpanded = !appearSticky || this.state.stickyPostExpanded || avoidClampUse;
        const inFeed = !isPreloaded;

        const postAuthorIsModerator = groupModerators
            ? authorIsModerator(post.author, groupModerators)
            : false;
        const isModerator = groupModerators
            ? authorIsModerator(requestUser, groupModerators)
            : false;
        const isGroupsAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const isBlogsAdmin = requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0;
        const isAuthor = isAuthenticatedAs(requestUser, post.author);
        const canChangeVisibility = showVisibilityMenu && (isAuthor || isBlogsAdmin);

        const hashizeFn = this.getHashizeFn(post.group ? post.group.id : null);

        // prepare post message body
        let bodyChunks: React.ReactChild[] = null;
        if (post.perexAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                linkedObjects: post.perexASTAdditionalData
                    ? post.perexASTAdditionalData.linkedObjects
                    : {},
                renderSpecialWikiLinks: false,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_LINK,
                hashizeFn,
            };
            bodyChunks = this.perexAstToHtmlCache(
                () => astToHtml(post.perexAST, additionalData),
                post.perexAST,
            );
        }

        const wrappedBodyChunks = (
            <ExpandableClamp expandLabel={SHOW_FULL_MESSAGE} clamp={13}>
                <div className={styles.BlogPost__body__content__text__perex}>
                    {appearSticky && (
                        <FontAwesomeIcon
                            icon={faThumbtack}
                            className={styles.BlogPost__body__content__stickyFa}
                        />
                    )}
                    <Emojify size={17}>{bodyChunks}</Emojify>
                </div>
            </ExpandableClamp>
        );

        let role;
        if (isGroupsAdmin || isBlogsAdmin) {
            role = "admin";
        } else if (isModerator) {
            role = "moderator";
        } else if (isAuthor) {
            role = "author";
        }

        const firstLineNote = post.type === BlogPostType.SHARE ? PB_SHARED_A_POST : null;

        const blogPostMenu = (
            <BlogPostMenu
                className={styles.BlogPost__body__meta__dropdownMenu}
                parentFeedItem={parentFeedItem}
                post={post}
                requestUser={requestUser}
                location={location}
                hasDelete
                hasEdit
                hasLeave={showGroup}
                hasHideGroupInFeed={showGroup}
                hasReport
                isAdmin={isBlogsAdmin || isGroupsAdmin}
                isAuthor={isAuthor}
                isModerator={isModerator}
                onChangeSticky={onChangeSticky}
                onDelete={onDelete}
                onGroupLeave={onGroupLeave}
                onHideGroupInFeed={onHideGroupInFeed}
                onIgnoreUser={onIgnoreUser}
                onReport={onReport}
                onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                onToggleMenuMore={onToggleMenuMore}
            />
        );

        return post.deletedAt && !this.state.stickyPostExpanded ? (
            this.renderDeletedPost(post, className)
        ) : (
            <div
                className={cx(
                    styles.BlogPost,
                    commentsOpened && styles["BlogPost--commentsOpen"],
                    className,
                )}
            >
                <div className={styles.BlogPost__body}>
                    {stickyPostExpanded &&
                        this.renderMeta(
                            post,
                            postAuthorIsModerator,
                            showGroup,
                            showVisibilityMenu,
                            canChangeVisibility,
                            onSetVisibility,
                            onToggleVisibilityMenuMore,
                            stickyPostExpanded,
                            firstLineNote,
                            blogPostMenu,
                            true,
                        )}

                    <div className={styles.BlogPost__body__content}>
                        <div className={styles.BlogPost__body__content__text}>
                            <div>
                                {post.deletedAt && (
                                    <DeletedPostAlert
                                        deletedByRole={post.deletedByRole}
                                        deletedBy={post.deletedBy?.username}
                                        deletedAt={post.deletedAt}
                                        reason={post.deletedReason}
                                    />
                                )}
                                {wrappedBodyChunks}
                            </div>
                        </div>
                        <div className={styles.BlogPost__body__content__sharedPostWrapper}>
                            {post.sharedPost && this.renderBlogPostInner(post.sharedPost)}
                        </div>
                    </div>
                </div>

                {this.renderComplaints(post, role)}

                {this.renderFooter(
                    stickyPostExpanded,
                    showLike,
                    post,
                    showReply,
                    commentsCount,
                    showShare,
                    complaintsCount,
                    isBlogsAdmin || isGroupsAdmin,
                    isModerator,
                )}

                {commentsOpened && (
                    <Comments
                        parentPost={post}
                        count={commentsCount}
                        design={CommentsDesign.Normal}
                        location={location}
                        history={history}
                        moderators={groupModerators}
                        hasBlueToolbar={!inFeed}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        isMobile={isMobile}
                        onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                    />
                )}
            </div>
        );
    }

    public renderAutomaticMessage() {
        const {
            className,
            commentsCount,
            commentsOpened,
            complaintsCount,
            forceEagerImages,
            groupModerators,
            isMobile,
            isPreloaded,
            location,
            history,
            onChangeSticky,
            onDelete,
            onGroupLeave,
            onHideGroupInFeed,
            onIgnoreUser,
            onSetDisplayingAtFeed,
            onReport,
            onSetVisibility,
            onToggleMenuMore,
            onToggleVisibilityMenuMore,
            parentFeedItem,
            post,
            requestUser,
            requestUserPermissions,
            showGroup,
            showSticky,
            showStrip,
            userAgent,
            imageComplaintsCounts,
        } = this.props;

        const avoidClampUse = userAgent?.flags?.buggyClamp;

        const showReply = true;
        const showLike = true;
        const showShare = false;

        const showVisibilityMenu = !!onToggleVisibilityMenuMore;
        const appearSticky = showSticky && post.sticky;
        const stickyPostExpanded = !appearSticky || this.state.stickyPostExpanded || avoidClampUse;
        const inFeed = !isPreloaded;

        const postAuthorIsModerator = groupModerators
            ? authorIsModerator(post.author, groupModerators)
            : false;
        const isModerator = groupModerators
            ? authorIsModerator(requestUser, groupModerators)
            : false;
        const isGroupsAdmin = requestUserPermissions.indexOf("forum.can_manage_forum") >= 0;
        const isBlogsAdmin = requestUserPermissions.indexOf("photoblog.can_manage_blogs") >= 0;
        const isAuthor = isAuthenticatedAs(requestUser, post.author);
        const canChangeVisibility = showVisibilityMenu && (isAuthor || isBlogsAdmin);

        let role;
        if (isGroupsAdmin || isBlogsAdmin) {
            role = "admin";
        } else if (isModerator) {
            role = "moderator";
        } else if (isAuthor) {
            role = "author";
        }

        const automaticMessageTitle = {
            CHANGED_AVATAR,
            CHILD_ADDED,
            CHILD_ADDED_SON,
            CHILD_ADDED_DAUGHTER,
            CHILD_EDITED_SON,
            CHILD_EDITED_DAUGHTER,
            EXPERIENCE_ADDED,
            RECIPE_ADDED,
            FORUM_THREAD_ADDED,
        }[post.automaticMessageTitleMsgid];
        const firstLineNote = post.type === BlogPostType.AUTOMATIC_MESSAGE && automaticMessageTitle;

        const blogPostMenu = (
            <BlogPostMenu
                className={styles.BlogPost__body__meta__dropdownMenu}
                parentFeedItem={parentFeedItem}
                post={post}
                requestUser={requestUser}
                location={location}
                hasDelete
                hasEdit
                hasLeave={showGroup}
                hasHideGroupInFeed={showGroup}
                hasReport
                isAdmin={isBlogsAdmin || isGroupsAdmin}
                isAuthor={isAuthor}
                isModerator={isModerator}
                onChangeSticky={onChangeSticky}
                onDelete={onDelete}
                onGroupLeave={onGroupLeave}
                onHideGroupInFeed={onHideGroupInFeed}
                onIgnoreUser={onIgnoreUser}
                onReport={onReport}
                onSetDisplayingAtFeed={onSetDisplayingAtFeed}
                onToggleMenuMore={onToggleMenuMore}
            />
        );

        return post.deletedAt && !this.state.stickyPostExpanded ? (
            this.renderDeletedPost(post, className)
        ) : (
            <div
                className={cx(
                    styles.BlogPost,
                    commentsOpened && styles["BlogPost--commentsOpen"],
                    className,
                )}
            >
                {this.renderShortMessageOrAlbumOrAutomsgInner(
                    post,
                    blogPostMenu,
                    isMobile,
                    forceEagerImages,
                    showStrip,
                    showGroup,
                    stickyPostExpanded,
                    appearSticky,
                    postAuthorIsModerator,
                    showVisibilityMenu,
                    canChangeVisibility,
                    onSetVisibility,
                    onToggleVisibilityMenuMore,
                    inFeed,
                    imageComplaintsCounts,
                    null,
                    false,
                    firstLineNote,
                    false,
                    null,
                    requestUser,
                )}

                {this.renderComplaints(post, role)}

                {this.renderFooter(
                    stickyPostExpanded,
                    showLike,
                    post,
                    showReply,
                    commentsCount,
                    showShare,
                    complaintsCount,
                    isBlogsAdmin || isGroupsAdmin,
                    isModerator,
                )}

                {commentsOpened && (
                    <Comments
                        parentPost={post}
                        count={commentsCount}
                        design={CommentsDesign.Normal}
                        location={location}
                        history={history}
                        moderators={groupModerators}
                        hasBlueToolbar={!inFeed}
                        requestUser={requestUser}
                        requestUserPermissions={requestUserPermissions}
                        isMobile={isMobile}
                        onSetTextareaRef={this.handleSetMessageFormTextareaRef}
                    />
                )}
            </div>
        );
    }

    private renderBlogPostInner(post: BlogPostEntity) {
        const { forceEagerImages, isMobile, requestUserPermissions } = this.props;

        return (
            ((post.type === BlogPostType.SHORT_MESSAGE || post.type === BlogPostType.ALBUM) &&
                this.renderShortMessageOrAlbumOrAutomsgInner(
                    post,
                    null,
                    isMobile,
                    forceEagerImages,
                )) ||
            (post.type === BlogPostType.ARTICLE &&
                this.renderArticleInner(
                    post,
                    null,
                    isMobile,
                    requestUserPermissions,
                    forceEagerImages,
                ))
        );
    }

    private renderShortMessageOrAlbumOrAutomsgInner(
        post: BlogPostEntity,
        blogPostMenu: any,
        isMobile: boolean,
        forceEagerImages: boolean = false,
        showStrip: boolean = true,
        showGroup: boolean = false,
        stickyPostExpanded: boolean = true,
        appearSticky: boolean = false,
        postAuthorIsModerator: boolean = false,
        showVisibilityMenu: boolean = false,
        canChangeVisibility: boolean = false,
        onSetVisibility: any = null,
        onToggleVisibilityMenuMore: any = null,
        inFeed: boolean = true,
        imageComplaintsCounts: any = null,
        newImages: any = null,
        wholeAlbumIsNew: boolean = null,
        firstLineNote: string = null,
        isGroupWithRecipes: boolean = false,
        onCreateRecipeFromPost: any = null,
        requestUser: UserEntity = null,
    ) {
        const hashizeFn = this.getHashizeFn(post.group ? post.group.id : null);

        // prepare post message body
        let bodyChunks: React.ReactChild[] = null;
        if (post.perexAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                customStyles: {
                    instagram: styles.BlogPost__body__content__instagram,
                    tiktok: styles.BlogPost__body__content__instagram,
                },
                linkedObjects: post.perexASTAdditionalData
                    ? post.perexASTAdditionalData.linkedObjects
                    : {},
                renderSpecialWikiLinks: false,
                renderLinksOfUntrustworthy: post.author.isUntrustworthy
                    ? url(forumUntrustworthyUserFeedUrl, { username: post.author.username })
                    : false,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_IFRAME,
                hashizeFn,
            };
            bodyChunks = this.perexAstToHtmlCache(
                () => astToHtml(post.perexAST, additionalData),
                post.perexAST,
            );
        }

        const wrappedBodyChunks = !!bodyChunks && (
            <>
                {appearSticky && !stickyPostExpanded ? (
                    <div className={styles.BlogPost__body__content__text__perex}>
                        <Clamp clamp={3}>
                            <div>
                                <FontAwesomeIcon
                                    icon={faThumbtack}
                                    className={styles.BlogPost__body__content__stickyFa}
                                />

                                <Emojify size={17}>{bodyChunks}</Emojify>
                            </div>
                        </Clamp>
                        <div
                            className={cx(styles.BlogPost__body__content__expand)}
                            onClick={this.handleExpand}
                        >
                            {SHOW_FULL_MESSAGE}
                        </div>
                    </div>
                ) : (
                    <ExpandableClamp expandLabel={SHOW_FULL_MESSAGE} clamp={13}>
                        <div className={styles.BlogPost__body__content__text__perex}>
                            {appearSticky && (
                                <FontAwesomeIcon
                                    icon={faThumbtack}
                                    className={styles.BlogPost__body__content__stickyFa}
                                />
                            )}
                            <Emojify size={17}>{bodyChunks}</Emojify>
                        </div>
                    </ExpandableClamp>
                )}
            </>
        );

        // Experience/StrollerReview photo props
        let expRevImgUrl = null;
        let expRevImgWidth = null;
        let expRevImgHeight = null;
        if (
            post.type === BlogPostType.AUTOMATIC_MESSAGE &&
            ["EXPERIENCE_ADDED"].includes(post.automaticMessageCode)
        ) {
            let expRevPhotofile;

            // photo of experience / review
            expRevPhotofile = post.experience?.photos?.length
                ? post.experience?.photos?.[0]?.photofile
                : post.review?.photos?.[0]?.photoFile;

            // photo of experience's / review's parent (i.e. article / stroller)
            if (!expRevPhotofile) {
                if (post.experience?.article)
                    expRevPhotofile =
                        findCoverPhotoEntityFromAst(
                            post.experience.article.bodyAST,
                            post.experience.article.photos,
                        ) ||
                        (post.experience.article.photos.length > 0
                            ? post.experience.article.photos?.[0]
                            : null);
            }
            if (expRevPhotofile) {
                expRevImgUrl = photoUrl(expRevPhotofile, getBestVersionForThumb(expRevPhotofile));
                expRevImgWidth = expRevPhotofile.width;
                expRevImgHeight = expRevPhotofile.height;
            } else if (post.stroller) {
                const revStrollerPhoto = post.stroller.previewPhoto || dummyPhoto;
                expRevImgUrl = revStrollerPhoto.url100x100;
                expRevImgWidth = revStrollerPhoto.originalWidth;
                expRevImgHeight = revStrollerPhoto.originalHeight;
            }
        }

        const canCreateRecipeFromPost =
            isGroupWithRecipes &&
            requestUser &&
            requestUser.username === post.author.username &&
            onCreateRecipeFromPost;

        return (
            <div className={styles.BlogPost__body}>
                {stickyPostExpanded &&
                    this.renderMeta(
                        post,
                        postAuthorIsModerator,
                        showGroup,
                        showVisibilityMenu,
                        canChangeVisibility,
                        onSetVisibility,
                        onToggleVisibilityMenuMore,
                        stickyPostExpanded,
                        firstLineNote,
                        blogPostMenu,
                    )}

                <div className={styles.BlogPost__body__content}>
                    <div className={styles.BlogPost__body__content__text}>
                        <div>
                            {post.deletedAt && (
                                <DeletedPostAlert
                                    deletedByRole={post.deletedByRole}
                                    deletedAt={post.deletedAt}
                                    deletedBy={post.deletedBy?.username}
                                    reason={post.deletedReason}
                                />
                            )}
                            {post.type !== BlogPostType.ALBUM && wrappedBodyChunks}
                            {canCreateRecipeFromPost && (
                                <Btn
                                    className={styles.BlogPost__createRecipeFromPost}
                                    type={BtnType.Link}
                                    label={RECIPE_CREATE_FROM_POST_HINT}
                                    onClick={this.handleCreateRecipeFromPost}
                                    sidePadding={BtnSidePadding.None}
                                />
                            )}
                        </div>
                    </div>

                    {stickyPostExpanded && post.stickerId && (
                        <div>
                            <Sticker lazy id={post.stickerId} size={StickerSize.Big} />
                        </div>
                    )}

                    {stickyPostExpanded &&
                        (post.images.length > 0 ||
                            post.type === BlogPostType.AUTOMATIC_MESSAGE) && (
                            <>
                                {post.type !== BlogPostType.ALBUM &&
                                    inFeed &&
                                    post.images.length > 1 && (
                                        <div className={styles.BlogPost__body__photos__count}>
                                            (
                                            {interpolate(counter_PHOTOS, {
                                                count: post.images.length,
                                                counter: post.images.length,
                                            })}
                                            )
                                        </div>
                                    )}
                                <BlogPostPhotos
                                    inFeed={inFeed}
                                    complaintsCounts={imageComplaintsCounts || emptyCounts}
                                    isMobile={isMobile}
                                    post={post}
                                    showStrip={showStrip}
                                    imagesSubset={newImages}
                                    forceEagerImages={forceEagerImages}
                                />
                                {post.type === BlogPostType.AUTOMATIC_MESSAGE && (
                                    <>
                                        {["CHILD_ADDED", "CHILD_EDITED"].includes(
                                            post.automaticMessageCode,
                                        ) &&
                                            post.extraData?.name && (
                                                <div className={styles.BlogPost__body__kidMeta}>
                                                    {post.extraData.gender === "M" ? (
                                                        <span
                                                            className={
                                                                styles.BlogPost__body__kidMeta__boyName
                                                            }
                                                        >
                                                            {post.extraData.name}
                                                        </span>
                                                    ) : (
                                                        <span
                                                            className={
                                                                styles.BlogPost__body__kidMeta__girlName
                                                            }
                                                        >
                                                            {post.extraData.name}
                                                        </span>
                                                    )}
                                                    {post.extraData.age && (
                                                        <>
                                                            &nbsp;-&nbsp;
                                                            <span
                                                                className={
                                                                    styles.BlogPost__body__kidMeta__age
                                                                }
                                                            >
                                                                {post.extraData.age}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        {["EXPERIENCE_ADDED"].includes(
                                            post.automaticMessageCode,
                                        ) && (
                                            <>
                                                {post.experience && (
                                                    <div
                                                        className={
                                                            styles.BlogPost__body__experience
                                                        }
                                                    >
                                                        {expRevImgUrl &&
                                                            expRevImgWidth &&
                                                            expRevImgHeight && (
                                                                <div
                                                                    className={
                                                                        styles.BlogPost__body__experience__image
                                                                    }
                                                                >
                                                                    <ImgCropped
                                                                        className={
                                                                            styles.BlogPost__body__experience__image__img
                                                                        }
                                                                        width={80}
                                                                        height={80}
                                                                        mode={ImgCropMode.Cover}
                                                                        imgUrl={expRevImgUrl}
                                                                        imgWidth={expRevImgWidth}
                                                                        imgHeight={expRevImgHeight}
                                                                        lazy
                                                                    />
                                                                </div>
                                                            )}
                                                        <div
                                                            className={
                                                                styles.BlogPost__body__experience__rightColumn
                                                            }
                                                        >
                                                            <h3
                                                                className={
                                                                    styles.BlogPost__body__experience__header
                                                                }
                                                            >
                                                                <Link
                                                                    to={getWikiArticleUrl(
                                                                        post.experience.article
                                                                            .category.slug,
                                                                        post.experience.article
                                                                            .slug,
                                                                    )}
                                                                >
                                                                    {post.experience.article.title}
                                                                </Link>
                                                            </h3>
                                                            {post.experience.starsCount > 0 &&
                                                                post.experience.article
                                                                    .experiencesStarsAllowed && (
                                                                    <StarsRating
                                                                        starsCount={
                                                                            post.experience
                                                                                .starsCount
                                                                        }
                                                                        className={
                                                                            styles.BlogPost__body__experience__stars
                                                                        }
                                                                    />
                                                                )}
                                                            <ExperienceBody
                                                                experience={post.experience}
                                                                showCompact
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {!!post.review && !!post.stroller && (
                                                    <div
                                                        className={
                                                            styles.BlogPost__body__experience
                                                        }
                                                    >
                                                        {expRevImgUrl &&
                                                            expRevImgWidth &&
                                                            expRevImgHeight && (
                                                                <div
                                                                    className={
                                                                        styles.BlogPost__body__experience__image
                                                                    }
                                                                >
                                                                    <ImgCropped
                                                                        className={
                                                                            styles.BlogPost__body__experience__image__img
                                                                        }
                                                                        width={80}
                                                                        height={80}
                                                                        mode={ImgCropMode.Cover}
                                                                        imgUrl={expRevImgUrl}
                                                                        imgWidth={expRevImgWidth}
                                                                        imgHeight={expRevImgHeight}
                                                                        lazy
                                                                    />
                                                                </div>
                                                            )}
                                                        <div
                                                            className={
                                                                styles.BlogPost__body__experience__rightColumn
                                                            }
                                                        >
                                                            <h3
                                                                className={
                                                                    styles.BlogPost__body__experience__header
                                                                }
                                                            >
                                                                <Link
                                                                    to={url(strollersDetailUrl, {
                                                                        slug: post.stroller.slug,
                                                                    })}
                                                                >
                                                                    {post.stroller.name}
                                                                </Link>
                                                            </h3>
                                                            <CollapsedReview
                                                                className={
                                                                    styles.BlogPost__body__experience__component
                                                                }
                                                                review={post.review}
                                                                stroller={post.stroller}
                                                                hideUser
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        {["RECIPE_ADDED"].includes(post.automaticMessageCode) &&
                                            post.advancedArticle &&
                                            post.advancedArticlePost &&
                                            (() => {
                                                const recipePhoto = post.advancedArticle.coverPhoto;
                                                const recipeTitle = getAllPlaintext(
                                                    post.advancedArticlePost.titleAST,
                                                );
                                                const recipeUrl = url(blogsAdvancedArticleUrl, {
                                                    username: post.user.username,
                                                    advancedArticleTypeSlug:
                                                        RECIPE_ARTICLE_TYPE_SLUG,
                                                    articleSlug: post.advancedArticlePost.slug,
                                                });
                                                const prepTime =
                                                    post.advancedArticle.preparationTimeMinutes ||
                                                    0;
                                                const cookTime =
                                                    post.advancedArticle.cookTimeMinutes || 0;
                                                const totalTime = prepTime + cookTime;
                                                const difficulty =
                                                    post.advancedArticle.difficultyLevel;
                                                const recipeImgUrl = recipePhoto
                                                    ? photoUrl(
                                                          recipePhoto,
                                                          getBestVersionForThumb(recipePhoto),
                                                      )
                                                    : null;

                                                const onelinerChunks = post.advancedArticle
                                                    .onelinerAST
                                                    ? astToHtml(
                                                          post.advancedArticle.onelinerAST,
                                                          defaultFilterAdditionalData,
                                                      )
                                                    : null;

                                                return (
                                                    <div
                                                        className={
                                                            styles.BlogPost__body__experience
                                                        }
                                                    >
                                                        {recipeImgUrl &&
                                                            recipePhoto.width &&
                                                            recipePhoto.height && (
                                                                <div
                                                                    className={
                                                                        styles.BlogPost__body__experience__image
                                                                    }
                                                                >
                                                                    <ImgCropped
                                                                        className={
                                                                            styles.BlogPost__body__experience__image__img
                                                                        }
                                                                        width={80}
                                                                        height={80}
                                                                        mode={ImgCropMode.Cover}
                                                                        imgUrl={recipeImgUrl}
                                                                        imgWidth={recipePhoto.width}
                                                                        imgHeight={
                                                                            recipePhoto.height
                                                                        }
                                                                        lazy
                                                                    />
                                                                </div>
                                                            )}
                                                        <div
                                                            className={
                                                                styles.BlogPost__body__experience__rightColumn
                                                            }
                                                        >
                                                            <h3
                                                                className={
                                                                    styles.BlogPost__body__experience__header
                                                                }
                                                            >
                                                                <Link to={recipeUrl}>
                                                                    {recipeTitle}
                                                                </Link>
                                                            </h3>
                                                            {onelinerChunks && (
                                                                <div
                                                                    className={
                                                                        styles.BlogPost__body__recipe__oneliner
                                                                    }
                                                                >
                                                                    {onelinerChunks}
                                                                </div>
                                                            )}
                                                            <div
                                                                className={
                                                                    styles.BlogPost__body__recipe__meta
                                                                }
                                                            >
                                                                {totalTime > 0 && (
                                                                    <span
                                                                        className={
                                                                            styles.BlogPost__body__recipe__metaItem
                                                                        }
                                                                    >
                                                                        <FontAwesomeIcon
                                                                            icon={faStopwatch}
                                                                        />
                                                                        &nbsp;
                                                                        {interpolate(
                                                                            number_MINUTES,
                                                                            {
                                                                                count: totalTime,
                                                                                number: totalTime,
                                                                            },
                                                                        )}
                                                                    </span>
                                                                )}
                                                                {difficulty !== null &&
                                                                    difficulty !== undefined && (
                                                                        <span
                                                                            className={
                                                                                styles.BlogPost__body__recipe__metaItem
                                                                            }
                                                                        >
                                                                            <Difficulty
                                                                                level={difficulty}
                                                                            />
                                                                        </span>
                                                                    )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                    </>
                                )}
                                {["FORUM_THREAD_ADDED"].includes(post.automaticMessageCode) &&
                                    post.forumThread && (
                                        <div className={styles.BlogPost__body__experience}>
                                            <div
                                                className={
                                                    styles.BlogPost__body__experience__rightColumn
                                                }
                                            >
                                                <h3
                                                    className={
                                                        styles.BlogPost__body__experience__header
                                                    }
                                                >
                                                    <Link
                                                        to={url(forumPostDetailUrl, {
                                                            topicSlug: post.forumThread.topic.slug,
                                                            postSlug: post.forumThread.slug,
                                                        })}
                                                    >
                                                        {post.forumThread.title}
                                                    </Link>
                                                </h3>
                                            </div>
                                        </div>
                                    )}
                                {post.type === BlogPostType.ALBUM && (
                                    <Link
                                        to={url(blogsAlbumDetailUrl, {
                                            postSlug: post.slug,
                                            username: post.author.username,
                                        })}
                                    >
                                        <div className={styles.BlogPost__body__photos__albumMeta}>
                                            <span
                                                className={
                                                    styles.BlogPost__body__photos__albumMeta__title
                                                }
                                            >
                                                {wrappedBodyChunks}
                                            </span>
                                            {wholeAlbumIsNew && (
                                                <>
                                                    <Dot />
                                                    <span
                                                        className={
                                                            styles.BlogPost__body__photos__newBadge
                                                        }
                                                    >
                                                        {NEW_POST}
                                                    </span>
                                                </>
                                            )}
                                            <>
                                                <Dot />
                                                {interpolate(counter_PHOTOS, {
                                                    count: post.images.length,
                                                    counter: post.images.length,
                                                })}
                                            </>
                                            {newImages && newImages.length > 0 && (
                                                <>
                                                    <Dot />
                                                    {interpolate(counter_NEW_PHOTOS, {
                                                        count: newImages.length,
                                                        counter: newImages.length,
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </Link>
                                )}
                            </>
                        )}
                </div>
            </div>
        );
    }

    private renderArticleInner(
        post: BlogPostEntity,
        blogPostMenu: any,
        isMobile: boolean,
        requestUserPermissions: any,
        forceEagerImages: boolean = false,
        showGroup: boolean = false,
        stickyPostExpanded: boolean = true,
        postAuthorIsModerator: boolean = false,
        showVisibilityMenu: boolean = false,
        canChangeVisibility: boolean = false,
        onSetVisibility: any = null,
        onToggleVisibilityMenuMore: any = null,
    ) {
        let articleTitleBodyChunks: React.ReactChild[] = null;
        let articleCoverBodyChunks: React.ReactChild[] = null;
        let articleContentBodyChunks: React.ReactChild[] = null;

        // Article's title
        if (post.perexAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                linkedObjects: post.perexASTAdditionalData
                    ? post.perexASTAdditionalData.linkedObjects
                    : {},
                renderSpecialWikiLinks: false,
                renderLinksOfUntrustworthy: post.author.isUntrustworthy
                    ? url(forumUntrustworthyUserFeedUrl, { username: post.author.username })
                    : false,
                renderSnippetObjectsAs: RenderSnippetAs.HTML_LINK,
                hashizeFn: null, // disallowing Link in Link (whole Article preview is in a Link)
            };
            articleTitleBodyChunks = this.perexAstToHtmlCache(
                () => astToHtml(post.perexAST, additionalData),
                post.perexAST,
            );
        }

        // Article's cover (photo/video) and body
        if (post.contentAST) {
            const mediaNodes = getAllDescendantsOfTypes(post.contentAST, [
                "pic",
                "youtube",
                "vimeo",
                "giphy",
            ]);
            const mediaNode = mediaNodes ? mediaNodes[0] : null;
            if (mediaNode) {
                const coverAdditionalData = {
                    ...defaultFilterAdditionalData,
                    photos: post.photos,
                    forceEagerImages,
                    linkedObjects: post.contentASTAdditionalData
                        ? post.contentASTAdditionalData.linkedObjects
                        : {},
                    renderLinksOfUntrustworthy: post.author.isUntrustworthy
                        ? url(forumUntrustworthyUserFeedUrl, { username: post.author.username })
                        : false,
                    hasResponsiveImages:
                        isMobile || requestUserPermissions.indexOf("wiki.can_edit_wiki") >= 0,
                };
                articleCoverBodyChunks = this.coverAstToHtmlCache(
                    () => astToHtml(mediaNode, coverAdditionalData),
                    mediaNode,
                );
            }

            const contentAdditionalData = {
                ...defaultFilterAdditionalData,
                photos: post.photos,
                showPhotosAndVideos: ShowPhotosAndVideos.NONE,
                countTopicContributors: post.countTopicContributors,
                renderLinksWithNofollow: true,
                renderLinksOfUntrustworthy: post.author.isUntrustworthy
                    ? url(forumUntrustworthyUserFeedUrl, { username: post.author.username })
                    : false,
                linkedObjects: post.contentASTAdditionalData
                    ? post.contentASTAdditionalData.linkedObjects
                    : {},
                hasResponsiveImages:
                    isMobile || requestUserPermissions.indexOf("wiki.can_edit_wiki") >= 0,
            };
            articleContentBodyChunks = this.contentAstToHtmlCache(
                () =>
                    astToHtml(
                        removeParagraphsAndLinesWithoutPlaintext(
                            cloneDeep(post.contentAST),
                            false,
                            6,
                        ) as AST,
                        contentAdditionalData,
                    ),
                post.contentAST,
            );
        }

        // Advanced article oneliner
        const { location } = this.props;
        const advancedArticle = post.advancedArticle;
        let oneliner: React.ReactChild[] = null;
        if (advancedArticle?.onelinerAST) {
            const additionalData: FilterAdditionalData = {
                ...defaultFilterAdditionalData,
                sourceApp: advancedArticle.category?.slug,
                sourceLocationPath: location.pathname,
                customStyles: {
                    paragraph: styles.BlogPost__advArticle__paragraph,
                },
            };
            oneliner = this.advancedArticleOnelinerAstToHtmlCache(
                () => astToHtml(advancedArticle.onelinerAST, additionalData),
                advancedArticle.onelinerAST,
            );
        }

        return (
            <div className={styles.BlogPost__body}>
                {stickyPostExpanded &&
                    this.renderMeta(
                        post,
                        postAuthorIsModerator,
                        showGroup,
                        showVisibilityMenu,
                        canChangeVisibility,
                        onSetVisibility,
                        onToggleVisibilityMenuMore,
                        stickyPostExpanded,
                        null,
                        blogPostMenu,
                    )}

                <div className={styles.BlogPost__body__content}>
                    {post.deletedAt && (
                        <DeletedPostAlert
                            deletedByRole={post.deletedByRole}
                            deletedAt={post.deletedAt}
                            deletedBy={post.deletedBy?.username}
                            reason={post.deletedReason}
                        />
                    )}

                    {post.advancedArticle ? (
                        // Recipe/Howto card
                        <GridRecipeCard
                            post={post}
                            hideUser
                            oneliner={oneliner}
                            coverPhotoMaxHeight={!isMobile ? 400 : undefined}
                        />
                    ) : (
                        // Regular article preview
                        <div className={styles.BlogPost__body__content__article}>
                            <Link
                                to={url(blogsArticleUrl, {
                                    username: post.author.username,
                                    articleSlug: post.slug,
                                })}
                            >
                                {articleCoverBodyChunks && (
                                    <div
                                        className={styles.BlogPost__body__content__article__cover}
                                        key={"articleCoverBodyChunks"}
                                    >
                                        {articleCoverBodyChunks}
                                    </div>
                                )}
                                <div
                                    className={styles.BlogPost__body__content__article__title}
                                    key={"articleTitleBodyChunks"}
                                >
                                    <Emojify size={17}>{articleTitleBodyChunks}</Emojify>
                                </div>
                            </Link>
                            <div
                                className={styles.BlogPost__body__content__article__content}
                                key={"articleContentBodyChunks"}
                            >
                                <Emojify size={17}>{articleContentBodyChunks}</Emojify>
                                <div
                                    className={
                                        styles.BlogPost__body__content__article__content__mask
                                    }
                                />
                            </div>
                            <div className={styles.BlogPost__body__content__article__readWhole}>
                                <Link
                                    to={url(blogsArticleUrl, {
                                        username: post.author.username,
                                        articleSlug: post.slug,
                                    })}
                                >
                                    {READ_WHOLE_ARTICLE}
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderFooter(
        stickyPostExpanded: boolean,
        showLike: boolean,
        post: BlogPostEntity,
        showReply: boolean,
        commentsCount: number,
        showShare: boolean,
        complaintsCount: number,
        isAdmin: boolean,
        isModerator: boolean,
    ) {
        if (!stickyPostExpanded) {
            return null;
        }

        if (post.deletedAt && !isAdmin && !isModerator) {
            return null;
        }

        return (
            <div className={styles.BlogPost__footer}>
                <div className={styles.BlogPost__footer__likes}>
                    {showLike && (
                        <Like
                            entity="photoblog.post"
                            id={post.id}
                            type={LikeType.IconCount}
                            className={styles.BlogPost__footer__likes__like}
                        />
                    )}
                </div>
                <div className={styles.BlogPost__footer__comments}>
                    {showReply && (
                        <CommentsButton
                            commentsCount={commentsCount}
                            className={cx(
                                styles.BlogPost__footer__comments__commentsButton,
                                complaintsCount &&
                                    (isAdmin
                                        ? styles[
                                              "BlogPost__footer__comments__commentsButton--has-complaints--admin"
                                          ]
                                        : styles[
                                              "BlogPost__footer__comments__commentsButton--has-complaints"
                                          ]),
                            )}
                            onClick={this.handleOnComments}
                        />
                    )}
                </div>
                {showShare && (
                    <div className={styles.BlogPost__footer__shares}>
                        <ShareButton
                            sharesCount={
                                post.sharedPost
                                    ? post.sharedPost.sharesCount || 0
                                    : post.sharesCount || 0
                            }
                            hideLabel
                            className={styles.BlogPost__footer__shares__sharesButton}
                            link={url(blogsPostShareUrl, {
                                postId: post.sharedPost ? post.sharedPost.id : post.id,
                            })}
                        />
                    </div>
                )}
            </div>
        );
    }

    private renderComplaints(post: BlogPostEntity, role) {
        const { postSubtreeComplaintIds } = this.props;
        return (
            <>
                {(role === "admin" || role === "moderator") && (
                    <Complaints
                        postSubtreeComplaintsData={
                            postSubtreeComplaintIds
                                ? {
                                      complaintIds: postSubtreeComplaintIds,
                                      parentPost: post,
                                  }
                                : null
                        }
                        className={styles.BlogPost__complaints}
                        id={post.id}
                        entity={ComplaintTargetType.PhotoblogPost}
                        deleteLabel={POST_REMOVE_COMPLAINTS}
                        adminTheme={role === "admin"}
                    />
                )}
            </>
        );
    }

    private renderDeletedPost(post: BlogPostEntity, className: string) {
        const { match, requestUserPermissions } = this.props;

        const isAdmin = userHasAdminPerm(!("group" in post) && !post.group, requestUserPermissions);

        return (
            // Deleted post (in collapsed form) is rendered only in groups, profile feed, blog's (main) feed. In other
            // cases it must dissapear when post became deleted. During fetch it is of course filtered on server side.
            // Note: deletedByRole == null (e.g.: automatic msg del. by system - if child/avatar is removed) hide always
            (post && "group" in post && !!post.group) ||
                (match.path === blogsProfileUrl && post.deletedByRole != null) ||
                (match.path === blogsFlaggedFeedUrl && post.deletedByRole != null) ||
                (match.path === blogsFlaggedGroupsFeedUrl && post.deletedByRole != null) ||
                (match.path === blogsUrl && isAdmin && post.deletedByRole != null) ? (
                <div className={cx(styles.BlogPost, styles["BlogPost--deleted"], className)}>
                    <Link
                        to={url(blogsProfileUrl, { username: post.author.username })}
                        className={styles.BlogPost__deletedAuthor}
                    >
                        {post.author.username}
                    </Link>
                    &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                    {postDeletedByToText(post.deletedByRole)}
                    &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                    <Btn
                        key="deleted-btn"
                        onClick={this.handleExpand}
                        label={SHOW}
                        sidePadding={BtnSidePadding.None}
                        type={BtnType.BlueTextLight}
                    />
                </div>
            ) : null
        );
    }

    private renderMeta(
        post: BlogPostEntity,
        postAuthorIsModerator: boolean,
        showGroup: boolean,
        showVisibilityMenu: boolean,
        canChangeVisibility: boolean,
        onSetVisibility: any,
        onToggleVisibilityMenuMore: any,
        stickyPostExpanded: boolean,
        firstLineNote: any,
        blogPostMenu: any,
        showjustDate: boolean = false,
    ) {
        const { disablePostAuthorBadges } = this.props;

        return (
            <div className={styles.BlogPost__body__meta}>
                <div className={styles.BlogPost__body__meta__avatarcol}>
                    <Link to={url(blogsProfileUrl, { username: post.author.username })}>
                        <AvatarImg
                            size={36}
                            user={post.author}
                            className={styles.BlogPost__body__meta__avatar}
                        />
                    </Link>
                </div>

                <div>
                    <Link
                        to={url(blogsProfileUrl, { username: post.author.username })}
                        className={styles.BlogPost__body__meta__author}
                    >
                        {post.author.username}
                    </Link>
                    {!!post.author.isAmbassadorOfBrandId && !disablePostAuthorBadges && (
                        <Badge
                            type={
                                post.author.isCommercialAmbassador
                                    ? BadgeType.CommercialAmbassador
                                    : BadgeType.Ambassador
                            }
                            className={styles.BlogPost__badge}
                        />
                    )}
                    {post.author.isVerifiedOrganization && !disablePostAuthorBadges && (
                        <Badge
                            type={BadgeType.VerifiedOrganization}
                            className={styles.BlogPost__badge}
                        />
                    )}
                    {post.author.isOfficialProfile && !disablePostAuthorBadges && (
                        <Badge
                            type={BadgeType.OfficialProfile}
                            className={styles.BlogPost__badge}
                        />
                    )}
                    {postAuthorIsModerator && (
                        <span
                            className={cx(
                                styles.BlogPost__body__meta__title,
                                styles["BlogPost__body__meta__title--moderator"],
                            )}
                        >
                            {GROUP_MODERATOR_SHORT}
                        </span>
                    )}
                    {showGroup && post.group && (
                        <span className={styles.BlogPost__body__meta__group}>
                            ›{" "}
                            <Link
                                className={styles.BlogPost__body__meta__group__name}
                                to={url(groupsGroupUrl, { groupId: post.group.id })}
                            >
                                {post.group.name}
                            </Link>
                        </span>
                    )}
                    {firstLineNote && (
                        <span className={styles.BlogPost__body__meta__note}>
                            &nbsp;
                            {firstLineNote}
                        </span>
                    )}
                    <div className={styles.BlogPost__body__meta__secondLine}>
                        {post.wasEdited && (
                            <>
                                {PB_POST_HAS_BEEN_EDITED}
                                <Dot />
                            </>
                        )}

                        <PostDateLink post={post} justDate={showjustDate} />

                        {post.type === BlogPostType.ARTICLE && (
                            <>
                                <Dot />
                                {interpolate(NUMBER_OF_VIEWS_count, {
                                    count: post.viewsCountTotal,
                                    counter: post.viewsCountTotal,
                                })}
                            </>
                        )}

                        {showVisibilityMenu && (
                            <>
                                <Dot />
                                <BlogPostVisibilityMenu
                                    enabled={canChangeVisibility}
                                    post={post}
                                    onSetVisibility={onSetVisibility}
                                    onToggleMenuMore={onToggleVisibilityMenuMore}
                                />
                            </>
                        )}
                    </div>
                </div>

                {stickyPostExpanded && blogPostMenu}
            </div>
        );
    }

    private handleExpand = () => {
        this.setState({ stickyPostExpanded: true });
    };

    private handleOnComments = () => {
        if (this.props.isPreloaded) {
            if (this.messageFormTextarea) {
                this.messageFormTextarea.focus();
            }
        } else {
            this.props.onSetCommentsOpened(this.props.post.id, !this.props.commentsOpened);
        }
    };

    private handleCreateRecipeFromPost = () => {
        if (this.props.onCreateRecipeFromPost) {
            this.props.onCreateRecipeFromPost(this.props.post.id);
        }
    };

    private handleSetMessageFormTextareaRef = (ref: any) => {
        this.messageFormTextarea = ref;
    };

    private getHashizeFn = (postGroupId: number) => {
        return (bodyChunks) =>
            hashize(bodyChunks, (hash) =>
                postGroupId
                    ? getGroupHashtagLink(postGroupId, hash)
                    : getShortMessageHashtagLink(hash),
            );
    };
}

function mapStateToProps(state: AppState, ownProps: OwnProps): StateProps {
    const { post } = ownProps;
    const blogPostState: BlogPostState = getBlogPostState(state, post.id);
    return {
        commentsOpened: blogPostState.commentsOpened,
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onSetCommentsOpened: blogPostSetCommentsOpened,
};

export default connect(mapStateToProps, mapDispatchToProps)(BlogPost);
